/**
 * Bundle 160 P0 - CompleteSignupModal.
 *
 * Rendered when /api/auth/me signals `signup_completion_required=true`
 * for the current user. Captures DOB + missing first/last name and
 * POSTs to /api/auth/complete-signup which invokes Bundle 149's shared
 * `require_adult_or_reject` + `require_full_name_or_reject`.
 *
 * Existing users created before Bundle 160 default the flag to
 * false (per user directive: no retro-blocking).
 *
 * Bundle 170 hotfix (prod fire response):
 *   - Fire 1a: previously only captured last_name + dob. Magic-link
 *     users whose account had no first_name (e.g. Umar Mahmood) hit
 *     "Please enter your first name" from the backend but had NO
 *     first_name input to fill. Now renders a first_name input
 *     whenever the current profile's first_name is empty / <2 chars,
 *     symmetric with the existing last_name branch.
 *   - Fire 1b: previously blocked all close paths (Esc, click-outside,
 *     X button). Users like Umar were trapped in a modal they could
 *     not fill AND could not close. Close is now allowed via
 *     `onDismissed`; server-side `signup_completion_required=true`
 *     remains so the next authenticated action re-triggers the modal.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/utils/api';
import { extractErrorMessage } from '@/utils/errorMessage';
import { toast } from 'sonner';

const CompleteSignupModal = ({ open, user, onCompleted, onDismissed }) => {
  const [dob, setDob] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const needsFirstName = !((user?.first_name || '').trim().length >= 2);
  const needsLastName = !((user?.last_name || '').trim().length >= 2);

  const submit = async () => {
    setError('');
    if (needsFirstName && !(firstName || '').trim()) {
      setError('Please enter your first name.');
      return;
    }
    if (needsLastName && !(lastName || '').trim()) {
      setError('Please enter your last name.');
      return;
    }
    if (!dob) {
      setError('Please enter your date of birth.');
      return;
    }
    setBusy(true);
    try {
      const body = { dob };
      if (needsFirstName) body.first_name = firstName.trim();
      if (needsLastName) body.last_name = lastName.trim();
      await api.post('/auth/complete-signup', body);
      toast.success('Signup complete. Welcome!');
      if (typeof onCompleted === 'function') onCompleted();
    } catch (e) {
      setError(extractErrorMessage(e,
        'Could not complete signup. Please retry.'));
    } finally { setBusy(false); }
  };

  const handleClose = () => {
    if (busy) return;
    if (typeof onDismissed === 'function') onDismissed();
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent
        className="max-w-md"
        data-testid="complete-signup-modal"
      >
        <DialogHeader>
          <DialogTitle>One quick step to finish setting up your account</DialogTitle>
          <DialogDescription>
            We need a couple of details before you can RSVP or manage your profile.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {needsFirstName ? (
            <div className="space-y-1.5">
              <Label htmlFor="csm-first-name">First name</Label>
              <Input
                id="csm-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Your first name"
                data-testid="complete-signup-first-name-input"
                autoFocus
              />
            </div>
          ) : null}
          {needsLastName ? (
            <div className="space-y-1.5">
              <Label htmlFor="csm-last-name">Last name</Label>
              <Input
                id="csm-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Your last name"
                data-testid="complete-signup-last-name-input"
              />
            </div>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="csm-dob">Date of birth</Label>
            <Input
              id="csm-dob"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              data-testid="complete-signup-dob-input"
            />
            <div className="text-xs text-neutral-500">
              Dostination is available to guests 18 and over.
            </div>
          </div>
          {error ? (
            <div
              className="text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-lg p-3"
              data-testid="complete-signup-error"
            >{error}</div>
          ) : null}
          <div
            className="text-xs text-neutral-500"
            data-testid="complete-signup-can-close-note"
          >
            You can close this and finish later. RSVPs and profile
            updates will remain paused until these fields are filled.
          </div>
        </div>
        <DialogFooter>
          <Button
            disabled={busy}
            onClick={submit}
            className="bg-primary-900 text-white hover:bg-primary-800 w-full"
            data-testid="complete-signup-submit"
          >
            {busy ? 'Saving...' : 'Complete signup'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteSignupModal;
